import {
    getAuth,
    type IdTokenResult,
    SAMLAuthProvider,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    type User,
    type UserCredential,
} from 'firebase/auth';

interface IDevCustomTokenResponse {
    token?: string;
    email?: string;
    uid?: string;
    message?: string;
}

/**
 * Enhanced Auth Manager that provides Firebase authentication
 * with token management and user data extraction
 * Based on the old auth-manager.ts implementation
 */
export class AuthManager {
    private token = '';

    /**
     * Log in with SAML provider (OneLogin)
     */
    async logIn(): Promise<any> {
        try {
            const provider = new SAMLAuthProvider('saml.onelogin');
            const result = await signInWithPopup(getAuth(), provider);

            console.group('🔐 [Auth Manager] SAML Login Successful:', result.user.email);
            console.groupEnd();
            return result;
        } catch (error) {
            console.error('🔐 [Auth Manager] SAML login failed:', error);
            throw error;
        }
    }

    /**
     * Log out current user
     */
    async logOut(): Promise<void> {
        try {
            const currentUser = getAuth().currentUser;
            console.log('🔐 [Auth Manager] Logging out user:', currentUser?.email || 'Unknown');
            await getAuth().signOut();
            console.log('🔐 [Auth Manager] ✅ User logged out successfully');
        } catch (error) {
            console.error('🔐 [Auth Manager] ❌ Logout failed:', error);
            throw error;
        }
    }

    /**
     * Log in with email and password
     */
    async logInWithEmailAndPassword(email: string, password: string): Promise<any> {
        try {
            return await signInWithEmailAndPassword(getAuth(), email, password);
        } catch (error) {
            console.error('Email/password login failed:', error);
            throw error;
        }
    }

    async logInWithDevCustomToken(): Promise<UserCredential> {
        const response = await fetch('/auth/dev/custom-token');
        const data = (await response.json().catch(() => ({}))) as IDevCustomTokenResponse;

        if (!response.ok || !data.token) {
            throw new Error(data.message ?? `RAM dev auth failed with status ${response.status}`);
        }

        const result = await signInWithCustomToken(getAuth(), data.token);
        console.log('[Auth Manager] RAM dev login successful:', data.email ?? result.user.email);
        return result;
    }

    /**
     * Get current user's Firebase ID token
     */
    async getToken(forceRefresh = false): Promise<string> {
        const currentUser = getAuth().currentUser;
        if (!currentUser) {
            throw new Error('User Not Authenticated');
        }

        try {
            this.token = await currentUser.getIdToken(forceRefresh);
            return this.token;
        } catch (error) {
            console.error('Failed to get token:', error);
            throw error;
        }
    }

    /**
     * Extract user data from ID token for password provider
     */
    getUserDataForPasswordProvider(idResult: IdTokenResult) {
        return {
            userId: idResult.claims.user_id as string,
            firstname: 'Test',
            lastname: 'User',
            email: idResult.claims.email as string,
            activeRole: 'test-user',
        };
    }

    /**
     * Extract user data from ID token for SAML provider
     * Handles Firebase's strange encoding for SAML attributes
     */
    getUserDataForSAMLProvider(idResult: IdTokenResult) {
        // Firebase returns everything in a strange format and umlauts not parsed
        // Most probably due to encoding on Firebase side which doesn't have configurations
        // This code should be fault-tolerant or the whole page will not load

        // Type assertion for Firebase custom claims since they're not properly typed
        const firebaseClaims = idResult.claims.firebase as any;

        let firstname = firebaseClaims?.['User.FirstName'] ?? firebaseClaims?.sign_in_attributes?.['User.FirstName'];
        let lastname = firebaseClaims?.['User.LastName'] ?? firebaseClaims?.sign_in_attributes?.['User.LastName'];

        // Try to decode URI components to fix encoding issues
        try {
            if (firstname) {
                firstname = decodeURIComponent(escape(firstname));
            }
            if (lastname) {
                lastname = decodeURIComponent(escape(lastname));
            }
        } catch (e) {
            // If decoding fails, use original values
            console.warn('Failed to decode names:', e);
        }

        return {
            userId: idResult.claims.user_id as string,
            firstname,
            lastname,
            email: firebaseClaims?.['User.email'] ?? (firebaseClaims?.sign_in_attributes?.['User.email'] as string),
            activeRole: (idResult.claims as any).activeRole,
        };
    }

    /**
     * Fetch user data from Firebase user object
     */
    async fetchUserData(user: User): Promise<object | null> {
        if (!user) {
            console.log('🔐 [Auth Manager] No user provided to fetchUserData');
            return null;
        }

        try {
            const idTokenResult = await user.getIdTokenResult();

            // Check sign-in provider to determine how to extract user data
            const firebaseClaims = idTokenResult.claims.firebase as any;
            const signInProvider = firebaseClaims?.sign_in_provider;

            let userData: {
                userId: string;
                firstname: string;
                lastname: string;
                email: string;
                activeRole: string;
            };
            if (signInProvider === 'password') {
                console.log('🔐 [Auth Manager] Using password provider data extraction');
                userData = this.getUserDataForPasswordProvider(idTokenResult);
            } else {
                userData = this.getUserDataForSAMLProvider(idTokenResult);
            }

            console.log('🔐 [Auth Manager] ✅ User data extracted successfully:');
            return userData;
        } catch (error) {
            console.error('🔐 [Auth Manager] ❌ Failed to fetch user data:', error);
            return null;
        }
    }

    /**
     * Get current user data
     * Uses a Promise-based approach to handle Firebase's async auth state
     */
    async getUserData(): Promise<object | null> {
        return new Promise((resolve, reject) => {
            const unsubscribe = getAuth().onAuthStateChanged(
                async user => {
                    unsubscribe();
                    if (user) {
                        try {
                            const userData = await this.fetchUserData(user);
                            resolve(userData);
                        } catch (error) {
                            reject(error);
                        }
                    } else {
                        resolve(null);
                    }
                },
                error => {
                    unsubscribe();
                    reject(error);
                },
            );
        });
    }

    /**
     * Get current user
     */
    getCurrentUser(): User | null {
        return getAuth().currentUser;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!getAuth().currentUser;
    }
}

/**
 * Singleton instance of AuthManager
 */
export const authManager = new AuthManager();

/**
 * Re-export for compatibility with existing code
 */
export default authManager;
